/**
 * Capacitor-specific Cognito session reader.
 *
 * Bypasses Amplify's `fetchAuthSession` on Capacitor and reads tokens
 * directly from localStorage. This exists because Capacitor cannot use
 * Amplify's `signInWithRedirect` (the WebView blocks `window.location.assign`),
 * so we own the OAuth dance ourselves (`startSsoRedirect` →
 * `exchangeCodeOnCapacitor` → `storeCognitoTokens`). Owning the writes means
 * we should own the reads too — half-using Amplify is the leaky path that
 * caused tokens to be wiped mid-login: Amplify's internal
 * refresh fails on our manually-stored tokens because we don't write
 * `userData`/device-tracking metadata, and `handleErrors` calls
 * `clearTokens()` to "clean up" — destroying the session we just stored.
 *
 * Web is untouched: it still goes through Amplify's `fetchAuthSession`.
 *
 * Token refresh: when the stored ID token is near expiration, this reader
 * POSTs `grant_type=refresh_token` to Cognito's `/oauth2/token` endpoint
 * and rewrites localStorage with the fresh tokens. If refresh fails, it
 * clears the local tokens and returns an empty session — the caller should
 * treat that as "session expired, re-SSO".
 */
import { cognitoConfig } from "./config";

const STORAGE_PREFIX = "CognitoIdentityServiceProvider";
/** Refresh ID token if it expires within this many seconds. */
const EXPIRATION_SKEW_SECONDS = 60;

interface JWTLike {
  payload: Record<string, unknown>;
  toString(): string;
}

/**
 * Shape-compatible subset of Amplify's `AuthSession` so callers reading
 * `session.tokens?.idToken?.payload?.email`, `idToken.toString()`, etc. work
 * unchanged.
 */
export interface CapacitorAuthSession {
  tokens?: {
    idToken?: JWTLike;
    accessToken?: JWTLike;
    refreshToken?: string;
  };
}

function decodeJwt(token: string): JWTLike {
  const [, payloadSegment = ""] = token.split(".");
  const normalized = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
  return {
    payload,
    toString: () => token,
  };
}

function isExpiredOrNearlyExpired(payload: Record<string, unknown>): boolean {
  const { exp } = payload;
  if (typeof exp !== "number") {
    return true;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp - EXPIRATION_SKEW_SECONDS < nowSeconds;
}

function getStorageKeys(username: string) {
  const prefix = `${STORAGE_PREFIX}.${cognitoConfig.clientId}`;
  return {
    idToken: `${prefix}.${username}.idToken`,
    accessToken: `${prefix}.${username}.accessToken`,
    refreshToken: `${prefix}.${username}.refreshToken`,
    clockDrift: `${prefix}.${username}.clockDrift`,
    lastAuthUser: `${prefix}.LastAuthUser`,
  };
}

function getLastAuthUser(): string | undefined {
  const key = `${STORAGE_PREFIX}.${cognitoConfig.clientId}.LastAuthUser`;
  return localStorage.getItem(key) ?? undefined;
}

function clearStoredTokens(username: string): void {
  const keys = getStorageKeys(username);
  localStorage.removeItem(keys.idToken);
  localStorage.removeItem(keys.accessToken);
  localStorage.removeItem(keys.refreshToken);
  localStorage.removeItem(keys.clockDrift);
  localStorage.removeItem(keys.lastAuthUser);
}

async function refreshTokensWithCognito(
  refreshToken: string
): Promise<{ idToken: string; accessToken: string } | undefined> {
  const response = await fetch(`https://${cognitoConfig.oauthDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: cognitoConfig.clientId,
      refresh_token: refreshToken,
    }),
  });
  if (!response.ok) {
    return undefined;
  }
  // Cognito's `/oauth2/token` response uses snake_case per the OAuth 2.0 spec.
  const data = (await response.json()) as Record<string, unknown>;
  const idToken = typeof data.id_token === "string" ? data.id_token : undefined;
  const accessToken = typeof data.access_token === "string" ? data.access_token : undefined;
  if (!idToken || !accessToken) {
    return undefined;
  }
  return { idToken, accessToken };
}

function persistRefreshedTokens(username: string, idToken: string, accessToken: string): void {
  const keys = getStorageKeys(username);
  localStorage.setItem(keys.idToken, idToken);
  localStorage.setItem(keys.accessToken, accessToken);
  // Cognito's refresh-token grant doesn't return a new refresh token by
  // default — keep the existing one in place.
}

/**
 * Returns the current Cognito session by reading directly from localStorage.
 * Refreshes tokens against Cognito's `/oauth2/token` endpoint if the stored
 * ID token is near expiration. Returns an empty session if no tokens exist
 * or refresh fails.
 *
 * Shape-compatible with Amplify's `fetchAuthSession` for the fields callers
 * actually use (`tokens.idToken.payload`, `tokens.idToken.toString()`,
 * `tokens.accessToken`).
 */
export async function readCapacitorCognitoSession(options?: {
  forceRefresh?: boolean;
}): Promise<CapacitorAuthSession> {
  const username = getLastAuthUser();
  if (!username) {
    return {};
  }

  const keys = getStorageKeys(username);
  const idTokenString = localStorage.getItem(keys.idToken);
  const accessTokenString = localStorage.getItem(keys.accessToken);
  const refreshToken = localStorage.getItem(keys.refreshToken) ?? undefined;

  if (!idTokenString || !accessTokenString) {
    return {};
  }

  const idToken = decodeJwt(idTokenString);
  const accessToken = decodeJwt(accessTokenString);

  const needsRefresh =
    options?.forceRefresh ||
    isExpiredOrNearlyExpired(idToken.payload) ||
    isExpiredOrNearlyExpired(accessToken.payload);

  if (!needsRefresh) {
    return { tokens: { idToken, accessToken, refreshToken } };
  }

  if (!refreshToken) {
    clearStoredTokens(username);
    return {};
  }

  const refreshed = await refreshTokensWithCognito(refreshToken).catch(() => undefined);
  if (!refreshed) {
    clearStoredTokens(username);
    return {};
  }

  persistRefreshedTokens(username, refreshed.idToken, refreshed.accessToken);
  return {
    tokens: {
      idToken: decodeJwt(refreshed.idToken),
      accessToken: decodeJwt(refreshed.accessToken),
      refreshToken,
    },
  };
}
